import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { StatusActiveIcon, StatusInactiveIcon } from '../icons/StatusIcons';
import { FieldLabel, FieldValue, SectionCard, SectionTitle } from '../Contacts/Sections/DetailFields';
import { AccessLevelCard } from '../common/AccessLevelCard';
import { SectionHeading } from '../common/SectionHeading';
import { formatUserId } from '../common/formatUserId';
import { useTranslation } from '@hooks/useTranslation';
import { isFieldStaffRole, SALES_TEAM_ROLES } from '../../constants';
import type { User } from '@api/users';

/** Until users carry a team, every account's access is to the portal itself. */
const ACCESS_PRODUCT = 'Grain Portal';

const CONTACT_SEPARATOR = ' · ';

const EMPTY_VALUE = '—';

interface UserAccountSectionProps {
  user: User;
}

/** Mirrors User::Roles::SALES_TEAM, which the server clears the flag against. */
function isSalesTeam(role: User['role']): boolean {
  return SALES_TEAM_ROLES.includes(role as (typeof SALES_TEAM_ROLES)[number]);
}

function displayName(user: User): string {
  return user.name ?? user.email;
}

function ProfileBlock({ user }: UserAccountSectionProps) {
  const { t } = useTranslation();
  const contactLine = [user.email, user.mobile_number].filter(Boolean).join(CONTACT_SEPARATOR);

  return (
    <div>
      <SectionHeading as="h4" rule={false}>
        {t('common.sections.profile')}
      </SectionHeading>

      {/* Breakpoints run later than the account page's: inside a 75vw drawer
          there is only ~576px to divide at the md viewport. */}
      <div className="mt-6 flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-12">
        <Avatar
          className="shrink-0"
          size="2xl"
          src={user.avatar_url ?? undefined}
          name={displayName(user)}
          alt={displayName(user)}
        />

        <div className="grid min-w-0 flex-1 grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h5 className="min-w-0 truncate font-inter text-lg font-semibold text-off-black">
                {displayName(user)}
              </h5>
              <Badge variant="tag">{formatUserId(user.id)}</Badge>
            </div>
            <p className="mt-2 break-words font-inter text-sm text-off-black">{contactLine}</p>
          </div>

          <div className="min-w-0">
            <FieldLabel>{t('form.labels.role')}</FieldLabel>
            <AccessLevelCard
              className="mt-2"
              product={ACCESS_PRODUCT}
              role={t(`roles.${user.role}`)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function OtherDetailsBlock({ user }: { user: User }) {
  const { t } = useTranslation();

  return (
    <div>
      <SectionHeading as="h4" rule={false}>
        {t('common.sections.other_details')}
      </SectionHeading>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <FieldLabel>{t('form.labels.shown_in_catering_referral')}</FieldLabel>
          <div className="mt-2">
            <Badge
              variant="status"
              tone={user.shown_in_catering_referral ? 'mint' : 'neutral'}
              size="sm"
              bordered
              icon={user.shown_in_catering_referral ? <StatusActiveIcon /> : <StatusInactiveIcon />}
            >
              {user.shown_in_catering_referral
                ? t('common.fields.enabled')
                : t('common.fields.disabled')}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffDetailsBlock({ user }: { user: User }) {
  const { t } = useTranslation();
  const confirmed = user.confirmation_status === 'confirmed';

  return (
    <div>
      <SectionHeading as="h4" rule={false}>
        {t('common.sections.staff_details')}
      </SectionHeading>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <FieldLabel>{t('form.labels.confirmation_status')}</FieldLabel>
          {user.confirmation_status ? (
            <div className="mt-2">
              <Badge
                variant="status"
                tone={confirmed ? 'mint' : 'neutral'}
                size="sm"
                bordered
                icon={confirmed ? <StatusActiveIcon /> : <StatusInactiveIcon />}
              >
                {t(`form.options.confirmation_status.${user.confirmation_status}`)}
              </Badge>
            </div>
          ) : (
            <FieldValue>{EMPTY_VALUE}</FieldValue>
          )}
        </div>
        <div>
          <FieldLabel>{t('form.labels.access_level')}</FieldLabel>
          <FieldValue>
            {user.access_level ? t(`form.options.access_level.${user.access_level}`) : EMPTY_VALUE}
          </FieldValue>
        </div>
        <div>
          <FieldLabel>{t('form.labels.employment_type')}</FieldLabel>
          <FieldValue>{t(`form.options.employment_type.${user.employment_type}`)}</FieldValue>
        </div>
        <div>
          <FieldLabel>{t('form.labels.nric')}</FieldLabel>
          <FieldValue>{user.nric ?? EMPTY_VALUE}</FieldValue>
        </div>
        <div>
          <FieldLabel>{t('form.labels.vehicle_number')}</FieldLabel>
          <FieldValue>{user.vehicle_number ?? EMPTY_VALUE}</FieldValue>
        </div>
      </div>
    </div>
  );
}

export function UserAccountSection({ user }: UserAccountSectionProps) {
  const { t } = useTranslation();

  return (
    <section>
      <SectionTitle>{t('common.sections.account')}</SectionTitle>
      <SectionCard>
        <ProfileBlock user={user} />
        {/* Off the sales team the block would be empty. */}
        {isSalesTeam(user.role) && (
          <>
            <div className="my-6 h-px w-full bg-dinnerplate-white" />
            <OtherDetailsBlock user={user} />
          </>
        )}
        {isFieldStaffRole(user.role) && (
          <>
            <div className="my-6 h-px w-full bg-dinnerplate-white" />
            <StaffDetailsBlock user={user} />
          </>
        )}
      </SectionCard>
    </section>
  );
}
